import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button, Input, Avatar, message, Spin, Alert } from "antd";
import {
  BankOutlined,
  DollarCircleOutlined,
  SearchOutlined,
  LeftOutlined,
} from "@ant-design/icons";
import { generateClient } from "aws-amplify/api";
import * as queries from "../graphql/queries";
import * as mutations from "../graphql/mutations";
import { getTableID } from "../Services/authServices";
import canadaFlag from "../Image/ca-circle.png";
import barbadosFlag from "../Image/flag/barbados.png";
import walletLogoIcon from "../Image/logo-icon.png";

const API = generateClient();
const getAvailableNewRecipientMethods = (senderCurrency, targetCurrency) => {
  if (senderCurrency === "CAD" && targetCurrency === "BBD") {
    return ["BajCan Wallet", "Bank Transfer", "Cash Pickup"];
  }
  return ["BajCan Wallet"];
};

const getDisplayName = (user) => {
  const first = (user?.firstName || "").trim();
  const last = (user?.lastName || "").trim();
  if (first || last) return `${first} ${last}`.trim();

  const name = (user?.name || "").trim();
  if (name) return name;

  return "BajCan User";
};

const getAccountTypeLabel = (user) => {
  if (user?.isOrganization || String(user?.businessName || "").trim()) {
    return "Business";
  }
  return "Personal";
};

const getFirstName = (user) => {
  const first = (user?.firstName || "").trim();
  if (first) return first;
  const name = (user?.name || "").trim();
  if (!name) return "BajCan";
  return name.split(" ")[0] || "BajCan";
};

const getLastName = (user) => {
  const last = (user?.lastName || "").trim();
  if (last) return last;
  const name = (user?.name || "").trim();
  if (!name.includes(" ")) return "User";
  return name.split(" ").slice(1).join(" ") || "User";
};

const formatPhoneNumber = (value) => {
  const digits = (value || "").replace(/\D/g, "");
  if (!digits) return "No phone number";

  if (digits.startsWith("1246") && digits.length === 11) {
    const local = digits.slice(4);
    return `+1 246 ${local.slice(0, 3)}-${local.slice(3)}`;
  }
  if (digits.startsWith("1") && digits.length === 11) {
    const local = digits.slice(1);
    return `+1 (${local.slice(0, 3)}) ${local.slice(3, 6)}-${local.slice(6)}`;
  }

  return value;
};


const getCountryFlag = (currencyCode) =>
  String(currencyCode || "").toUpperCase() === "CAD" ? canadaFlag : barbadosFlag;

const NewRecipientSetupModal = ({
  open,
  onCancel,
  onComplete,
  senderWalletCurrency,
  presetRecipientCurrency,
}) => {
  const [modalStep, setModalStep] = useState(1);
  const [newRecipientCurrency, setNewRecipientCurrency] = useState("BBD");
  const [newRecipientDeliveryMethod, setNewRecipientDeliveryMethod] =
    useState("BajCan Wallet");
  const [searchEmail, setSearchEmail] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [searchResult, setSearchResult] = useState(null);
  const [selectedSearchResult, setSelectedSearchResult] = useState(false);
  const [searchFeedback, setSearchFeedback] = useState(null);

  useEffect(() => {
    if (!open) return;
    const normalizedPresetCurrency = String(presetRecipientCurrency || "").toUpperCase();
    const hasPresetCurrency =
      normalizedPresetCurrency === "CAD" || normalizedPresetCurrency === "BBD";
    setModalStep(hasPresetCurrency ? 2 : 1);
    setNewRecipientCurrency(hasPresetCurrency ? normalizedPresetCurrency : "BBD");
    setNewRecipientDeliveryMethod("BajCan Wallet");
    setSearchEmail("");
    setSearchLoading(false);
    setAddLoading(false);
    setSearchResult(null);
    setSelectedSearchResult(false);
    setSearchFeedback(null);
  }, [open, presetRecipientCurrency]);

  const availableNewRecipientMethods = useMemo(
    () =>
      getAvailableNewRecipientMethods(
        String(senderWalletCurrency || "CAD").toUpperCase(),
        newRecipientCurrency,
      ),
    [newRecipientCurrency, senderWalletCurrency],
  );

  useEffect(() => {
    if (!availableNewRecipientMethods.includes(newRecipientDeliveryMethod)) {
      setNewRecipientDeliveryMethod(availableNewRecipientMethods[0]);
    }
  }, [availableNewRecipientMethods, newRecipientDeliveryMethod]);

  const validateEmail = (value) =>
    /^\S+@\S+\.\S+$/.test(String(value || "").trim());

  const findUserByEmail = async () => {
    const email = String(searchEmail || "").trim().toLowerCase();

    const tableID = await getTableID();
    if (!tableID) {
      setSearchFeedback({
        type: "error",
        message: "We could not verify your account right now.",
      });
      return null;
    }

    const userLookupResponse = await API.graphql({
      query: queries.userByEmail,
      variables: { email, limit: 5 },
    });

    const users = (userLookupResponse?.data?.userByEmail?.items || []).filter(
      (user) => user?.id && user.id !== tableID,
    );

    if (!users.length) return null;

    const usersWithWallets = await Promise.all(
      users.map(async (user) => {
        const walletResponse = await API.graphql({
          query: queries.walletsByUserID,
          variables: { userID: user.id, limit: 3 },
        });
        const matchedWallet = (
          walletResponse?.data?.walletsByUserID?.items || []
        ).find(
          (wallet) =>
            String(wallet?.currency || "").toUpperCase() === newRecipientCurrency,
        );
        if (!matchedWallet) return null;

        const existingRes = await API.graphql({
          query: queries.recipientsByUserID,
          variables: {
            userID: tableID,
            filter: {
              or: [
                { recipientWalletID: { eq: matchedWallet.id } },
                {
                  email: { eq: user.email },
                  recipientDeliveryMethod: { eq: "BajCan Wallet" },
                },
              ],
            },
            limit: 100,
          },
        });
        const isAlreadyAdded =
          (existingRes?.data?.recipientsByUserID?.items || []).length > 0;

        return { user, wallet: matchedWallet, isAlreadyAdded };
      }),
    );

    return usersWithWallets.find(Boolean) || null;
  };

  const handleSearch = async () => {
    if (!validateEmail(searchEmail)) {
      setSearchFeedback({
        type: "error",
        message: "Enter the recipient's email address to search for their BajCan wallet.",
      });
      return;
    }

    setSearchLoading(true);
    setSearchResult(null);
    setSelectedSearchResult(false);
    setSearchFeedback(null);
    try {
      const found = await findUserByEmail();
      if (!found) {
        setSearchFeedback({
          type: "invite",
          title: `No ${newRecipientCurrency} wallet found`,
          message:
            "We could not find a BajCan wallet for this email address.",
          note: "Ask the recipient to join BajCan or confirm the email address and try again.",
        });
        return;
      }
      setSearchResult(found);
    } catch (error) {
      console.error("Error searching BajCan user:", error);
      setSearchFeedback({
        type: "error",
        message: "We could not complete the wallet search right now. Please try again.",
      });
    } finally {
      setSearchLoading(false);
    }
  };

  const handleAddWalletRecipient = async () => {
    if (!searchResult?.user || !searchResult?.wallet) return;
    if (!selectedSearchResult) {
      message.error("Please select a user first.");
      return;
    }
    if (searchResult.isAlreadyAdded) return;
    setAddLoading(true);
    try {
      const tableID = await getTableID();
      if (!tableID) {
        message.error("Unable to identify current user.");
        return;
      }

      const user = searchResult.user;
      const isBusiness = Boolean(user?.isOrganization || user?.businessName);
      const input = {
        firstName: getFirstName(user),
        lastName: getLastName(user),
        phoneNumber: user?.phoneNumber || "",
        email: user?.email || "",
        recipientDeliveryMethod: "BajCan Wallet",
        isBusiness,
        businessName: user?.businessName || "",
        addressState: user?.province || "",
        addressCity: user?.city || "",
        addressStreet: user?.street || "",
        addressCountry:
          user?.country ||
          (newRecipientCurrency === "CAD" ? "Canada" : "Barbados"),
        addressZipCode: user?.pincode || "",
        userID: tableID,
        isRecipientVerified: true,
        typeOfRecipient: isBusiness ? "Business" : "Personal",
        isWalletRecipient: true,
        recipientWalletID: searchResult.wallet.id,
      };

      await API.graphql({
        query: mutations.createRecipient,
        variables: { input },
      });

      message.success("Wallet recipient added.");
      onComplete({ recipientAdded: true });
    } catch (error) {
      console.error("Error adding wallet recipient:", error);
      message.error("Unable to add recipient. Please try again.");
    } finally {
      setAddLoading(false);
    }
  };

  const handleContinue = () => {
    if (modalStep === 1) {
      setModalStep(2);
      return;
    }

    if (modalStep === 2 && newRecipientDeliveryMethod === "BajCan Wallet") {
      setModalStep(3);
      return;
    }

    onComplete({
      recipientCurrency: newRecipientCurrency.toLowerCase(),
      recipientDeliveryMethod: newRecipientDeliveryMethod,
    });
  };

  const handleBackStep = () => {
    if (modalStep <= 1) return;
    const hasPresetCurrency =
      String(presetRecipientCurrency || "").toUpperCase() === "CAD" ||
      String(presetRecipientCurrency || "").toUpperCase() === "BBD";
    if (hasPresetCurrency && modalStep === 2) {
      onCancel();
      return;
    }
    if (modalStep === 3) {
      setSearchEmail("");
      setSearchResult(null);
      setSearchLoading(false);
      setAddLoading(false);
      setSelectedSearchResult(false);
    }
    setModalStep((prev) => Math.max(1, prev - 1));
  };

  const footer =
    modalStep === 3 ? (
      <Button
        key="add-recipient"
        type="primary"
        size="large"
        className="signupbtn recipientCurrencyContinueBtn"
        onClick={handleAddWalletRecipient}
        loading={addLoading}
        disabled={!searchResult || !selectedSearchResult || searchResult.isAlreadyAdded}
      >
        Add Recipient
      </Button>
    ) : (
      <Button
        key="continue"
        type="primary"
        size="large"
        className="signupbtn recipientCurrencyContinueBtn"
        onClick={handleContinue}
      >
        {modalStep === 1 ? "Next" : "Continue"}
      </Button>
    );

  const stepTitle =
    modalStep === 1
      ? "Select Recipient Currency"
      : modalStep === 2
        ? "Select Delivery Method"
        : "Search BajCan Wallet";

  const modalHeaderTitle = (
    <div className="recipientCurrencyTitleRow">
      {modalStep > 1 && (
        <Button
          type="text"
          icon={<LeftOutlined />}
          onClick={handleBackStep}
          className="recipientCurrencyBackBtn"
        />
      )}
      <span className="recipientCurrencyTitleText">{stepTitle}</span>
    </div>
  );

  return (
    <Modal
      title={modalHeaderTitle}
      open={open}
      onCancel={onCancel}
      className="recipientCurrencyModal"
      footer={footer}
    >
      {(modalStep === 1 || modalStep === 2) && (
        <div className="recipientCurrencyModalGroup">
          {modalStep === 1 && (
            <>
              <button
                type="button"
                className={`recipientCurrencyOptionCard ${
                  newRecipientCurrency === "BBD"
                    ? "recipientCurrencyOptionCardActive"
                    : ""
                }`}
                onClick={() => setNewRecipientCurrency("BBD")}
              >
                <img src={barbadosFlag} alt="Barbados" />
                <div className="recipientCurrencyOptionContent">
                  <h4>BBD</h4>
                  <p>Barbados Dollar</p>
                </div>
                <span className="recipientCurrencyOptionIndicator" />
              </button>

              <button
                type="button"
                className={`recipientCurrencyOptionCard ${
                  newRecipientCurrency === "CAD"
                    ? "recipientCurrencyOptionCardActive"
                    : ""
                }`}
                onClick={() => setNewRecipientCurrency("CAD")}
              >
                <img src={canadaFlag} alt="Canada" />
                <div className="recipientCurrencyOptionContent">
                  <h4>CAD</h4>
                  <p>Canadian Dollar</p>
                </div>
                <span className="recipientCurrencyOptionIndicator" />
              </button>
            </>
          )}

          {modalStep === 2 && (
            <>
              <button
                type="button"
                className={`recipientCurrencyOptionCard ${
                  newRecipientDeliveryMethod === "BajCan Wallet"
                    ? "recipientCurrencyOptionCardActive"
                    : ""
                }`}
                onClick={() => setNewRecipientDeliveryMethod("BajCan Wallet")}
              >
                <span className="recipientCurrencyModeIcon">
                  <img
                    src={walletLogoIcon}
                    alt="BajCan Wallet"
                    className="recipientMethodBrandLogo"
                  />
                </span>
                <div className="recipientCurrencyOptionContent">
                  <h4>BajCan Wallet</h4>
                  <p>Wallet-to-wallet transfer</p>
                </div>
                <span className="recipientCurrencyOptionIndicator" />
              </button>

              <button
                type="button"
                className={`recipientCurrencyOptionCard ${
                  newRecipientDeliveryMethod === "Bank Transfer"
                    ? "recipientCurrencyOptionCardActive"
                    : ""
                } ${
                  availableNewRecipientMethods.includes("Bank Transfer")
                    ? ""
                    : "recipientCurrencyOptionCardDisabled"
                }`}
                onClick={() => {
                  if (!availableNewRecipientMethods.includes("Bank Transfer"))
                    return;
                  setNewRecipientDeliveryMethod("Bank Transfer");
                }}
              >
                <span className="recipientCurrencyModeIcon">
                  <BankOutlined />
                </span>
                <div className="recipientCurrencyOptionContent">
                  <h4>Bank Transfer</h4>
                  <p>
                    {availableNewRecipientMethods.includes("Bank Transfer")
                      ? "Deposit to recipient bank account"
                      : `Unavailable in ${
                          newRecipientCurrency === "CAD"
                            ? "Canada"
                            : "Barbados"
                        }`}
                  </p>
                </div>
                <span className="recipientCurrencyOptionIndicator" />
              </button>

              <button
                type="button"
                className={`recipientCurrencyOptionCard ${
                  newRecipientDeliveryMethod === "Cash Pickup"
                    ? "recipientCurrencyOptionCardActive"
                    : ""
                } ${
                  availableNewRecipientMethods.includes("Cash Pickup")
                    ? ""
                    : "recipientCurrencyOptionCardDisabled"
                }`}
                onClick={() => {
                  if (!availableNewRecipientMethods.includes("Cash Pickup"))
                    return;
                  setNewRecipientDeliveryMethod("Cash Pickup");
                }}
              >
                <span className="recipientCurrencyModeIcon">
                  <DollarCircleOutlined />
                </span>
                <div className="recipientCurrencyOptionContent">
                  <h4>Cash Pickup</h4>
                  <p>
                    {availableNewRecipientMethods.includes("Cash Pickup")
                      ? "Recipient collects cash at partner location"
                      : `Unavailable in ${
                          newRecipientCurrency === "CAD"
                            ? "Canada"
                            : "Barbados"
                        }`}
                  </p>
                </div>
                <span className="recipientCurrencyOptionIndicator" />
              </button>
            </>
          )}
        </div>
      )}

      {modalStep === 3 && (
        <div className="recipientCurrencyModalGroup">
          <div className="recipientWalletSearchRow">
            <Input
              size="large"
              value={searchEmail}
              onChange={(event) => setSearchEmail(event.target.value)}
              onPressEnter={handleSearch}
              placeholder="Enter recipient email"
              prefix={<SearchOutlined style={{ color: "#00267F" }} />}
            />
            <Button
              type="primary"
              size="large"
              onClick={handleSearch}
              loading={searchLoading}
              className="signupbtn recipientWalletSearchBtn"
            >
              Search
            </Button>
          </div>

          {searchLoading && (
            <div className="recipientWalletSearchLoading">
              <Spin />
            </div>
          )}

          {!searchLoading && searchFeedback?.type === "error" && (
            <Alert
              type="error"
              showIcon
              message={searchFeedback.message}
              className="recipientWalletSearchAlert"
            />
          )}

          {!searchLoading && searchFeedback?.type === "invite" && (
            <div className="recipientWalletSearchInvite">
              <div className="recipientWalletSearchInviteTitle">
                {searchFeedback.title}
              </div>
              <p>{searchFeedback.message}</p>
              <span>{searchFeedback.note}</span>
            </div>
          )}

          {!searchLoading && searchResult?.user && (
            <div
              className={`recipientWalletSearchResult ${
                selectedSearchResult ? "recipientWalletSearchResultSelected" : ""
              } ${searchResult.isAlreadyAdded ? "recipientWalletSearchResultDisabled" : ""}`}
              onClick={() => !searchResult.isAlreadyAdded && setSelectedSearchResult(true)}
              role="button"
              tabIndex={0}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  if (!searchResult.isAlreadyAdded) setSelectedSearchResult(true);
                }
              }}
            >
              <div className="recipientWalletSearchResultMain">
                <div className="recipientAvatarWrap">
                  <Avatar
                    size={60}
                    src={`https://api.dicebear.com/7.x/initials/svg?seed=${getDisplayName(searchResult.user)}`}
                    className="recipientAvatar"
                  />
                  <span className="recipientCurrencyBubble" title={newRecipientCurrency}>
                    <img
                      src={getCountryFlag(newRecipientCurrency)}
                      alt={newRecipientCurrency}
                      className="recipientCurrencyBubbleFlag"
                    />
                  </span>
                </div>
                <div className="recipientWalletSearchResultBody">
                  <div className="recipientItemTopRow">
                    <div className="recipientItemName">
                      {getDisplayName(searchResult.user)}
                    </div>
                    {searchResult.isAlreadyAdded ? (
                      <span className="recipientTypePill recipientAlreadyAddedPill">
                        Already Added
                      </span>
                    ) : (
                      <span className="recipientTypePill">
                        {getAccountTypeLabel(searchResult.user)}
                      </span>
                    )}
                  </div>
                  <div className="recipientItemMeta">
                    {searchResult.user.email}
                  </div>
                  <div className="recipientItemMeta">
                    {formatPhoneNumber(searchResult.user.phoneNumber)}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

export default NewRecipientSetupModal;
